import React, { useState } from "react";

import { useMutation } from "@apollo/client";
import { Box, Theme, Typography, useTheme } from "@mui/material";
import { parseISO } from "date-fns";
import { useParams } from "react-router-dom";
import { makeStyles } from "tss-react/mui";

import { graphql } from "@/gql";
import { PastLogFragment } from "@/gql/graphql";
import useDialogState from "@/hooks/useDialogState";
import useGlobalSnackbar from "@/hooks/useGlobalSnackbar";
import { format, formatISODate } from "@/utils/datetime";
import { displayError } from "@/utils/errors";
import { FragmentType } from "@/utils/type";

import EditCurrentWeightModal from "./EditCurrentWeightModal";
import OptionMenu from "./OptionMenu";
import ScaleCircularIcon from "../../../../../../components/Icons/ScaleCircularIcon";
import { clientWeightLogsPageQuery } from "../ClientWeightLogsPage";

const useStyles = makeStyles()((theme: Theme) => ({
  root: {
    padding: theme.spacing(2),
    maxHeight: 350,
    overflowY: "auto",
  },
  item: {
    display: "flex",
    alignItems: "center",
    margin: theme.spacing(2, 0),
  },
  noSpace: {
    margin: 0,
    padding: 0,
  },
  pastLogsHeadContainer: {
    display: "flex",
    justifyContent: "space-between",
  },
}));

interface Props {
  pastLogs: FragmentType<typeof pastLogsFragment>[];
}

const pastLogsFragment = graphql(`
  fragment PastLog on BodyMeasurement {
    id
    attr
    value
    date
    createdAt
  }
`);

const deleteCurrentWeightMutation = graphql(`
  mutation DeleteCurrentWeight2($input: CreateBodyMeasurementsInput!) {
    createBodyMeasurements(input: $input) {
      bodyMeasurement {
        date
        weightKg
      }
    }
  }
`);

const PastLogs: React.FC<Props> = ({ pastLogs }) => {
  const { clientId, date } = useParams<{ clientId: string; date: string }>();
  const [deleteCurrentWeight] = useMutation(deleteCurrentWeightMutation);
  const snackbar = useGlobalSnackbar();
  const [log, setlog] = useState<PastLogFragment | null>(null);
  const { classes } = useStyles();
  const theme = useTheme();
  const editPastLog = useDialogState();

  const _handleEdit = (log: PastLogFragment) => {
    setlog(log);
    editPastLog.show();
  };

  const _handleDelete = (id?: string) => {
    deleteCurrentWeight({
      variables: {
        input: {
          clientId: clientId,
          attributes: [{ id: id, _destroy: true }],
        },
      },
      refetchQueries: [
        {
          query: clientWeightLogsPageQuery,
          variables: {
            clientId: clientId ?? "",
            date: date ?? formatISODate(new Date()),
          },
        },
      ],
    })
      .then(() => {
        snackbar.open("Current weight deleted successfully");
      })
      .catch((e) => {
        snackbar.open(displayError(e, "Unable to delete current weight"));
      });
  };

  return (
    <div className={classes.root}>
      {pastLogs?.length === 0
        ? "No logs yet"
        : pastLogs?.map((log: PastLogFragment, index: number) => (
            <div className={classes.item} key={index}>
              <ScaleCircularIcon fontSize="large" />
              <Box marginRight={theme.spacing(1)} />
              <div>
                <Typography
                  variant="body1"
                  fontWeight="bold"
                  className={classes.noSpace}
                >{`${log.value} Kilograms`}</Typography>
                <Box>
                  <Typography variant="caption" color="textSecondary">
                    {format({ datetime: log.createdAt }, "hh:mm aaa")}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {` - ${format({ date: log.date }, "d MMM, yyyy")}`}
                  </Typography>
                </Box>
              </div>
              <OptionMenu
                onEdit={() => _handleEdit(log)}
                onDelete={() => _handleDelete(log.id)}
              />
            </div>
          ))}

      <EditCurrentWeightModal
        visibility={editPastLog.shown}
        onClose={editPastLog.hide}
        currentWeightId={log?.id ?? ""}
        weight={log?.value ?? 0}
        weightDate={new Date(log?.date ?? "") ?? new Date()}
      />
    </div>
  );
};

export default PastLogs;
